/**
 * Draft persistence for the /apply wizard.
 *
 * Today the form holds every answer in memory, so any navigation away
 * destroys it — the comment at ApplyNow.js:580-583 shows this has already
 * bitten, which is why the Terms link opens in a new tab. This is the fix
 * for the underlying fragility rather than a workaround for one instance
 * of it.
 *
 * Every localStorage access is wrapped: it throws in Safari private mode
 * and when storage is full or disabled by policy, and a saved draft is a
 * nicety that must never be able to break the form.
 */

export const DRAFT_KEY = 'mcl_apply_draft_v1';

/** Seven days, not the 30 of the draft-claim window. This is PII sitting
 *  on a possibly shared device, and a half-finished form stops being
 *  worth resuming long before a submitted application stops being worth
 *  claiming. */
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Never written to disk, whatever the caller passes. `form` holds no
 *  password today; this keeps that true if it ever does. */
const NEVER_PERSIST = ['password', 'confirmPassword'];

/** Fields that count as the applicant having actually done something.
 *
 *  caseType is deliberately absent. A case-type landing page links here as
 *  /apply?type=dog_bite, which pre-selects it before the visitor touches
 *  anything — counting that as progress would announce "we saved your
 *  progress" to someone who has typed nothing. contactEmail is absent for
 *  the same reason: it defaults to true. */
const CONTENT_FIELDS = [
  'requestedAmount',
  'accidentState',
  'email',
  'phone',
  'attorneyName',
  'attorneyEmail',
  'lawFirmName',
  'firstName',
  'lastName',
];

/** True when a form holds anything worth saving or announcing.
 *
 *  Without this, merely visiting /apply seeds an empty draft on the save
 *  debounce, and every later visit greets the visitor with a restore notice
 *  for a form they never filled in. */
export const hasDraftContent = (form) =>
  !!form && CONTENT_FIELDS.some((key) => {
    const value = form[key];
    return typeof value === 'string' && value.trim() !== '';
  });

export const isDraftFresh = (savedAt, now) => {
  if (typeof savedAt !== 'number') return false;
  const age = now - savedAt;
  // A negative age means the clock moved. Expire rather than trust it.
  return age >= 0 && age < DRAFT_TTL_MS;
};

export const clearDraft = () => {
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch (e) {
    // Storage unavailable. Nothing to do.
  }
};

export const readDraft = (now = Date.now()) => {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    if (!parsed.form || typeof parsed.form !== 'object') return null;

    if (!isDraftFresh(parsed.savedAt, now)) {
      clearDraft();
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
};

export const writeDraft = (form, step, now = Date.now()) => {
  try {
    const safe = { ...form };
    NEVER_PERSIST.forEach((key) => delete safe[key]);
    window.localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({ form: safe, step, savedAt: now }),
    );
  } catch (e) {
    // Storage unavailable or full. The form still works.
  }
};
